import React from "react";

/**
 * Logos oficiales simplificados para cada método de pago. Usa SVG inline
 * (en vez de URLs externas) para no depender de CDN terceros y asegurar
 * que siempre se rendericen incluso sin conectividad.
 *
 * Los wordmarks de BBVA y Santander son recreaciones tipográficas de
 * sus marcas. Stripe y HSBC usan sus logos oficiales (formas
 * geométricas propias).
 *
 * Uso: <PaymentLogo id="bbva" size={32} />
 *   - `size`: altura en px (width se ajusta al aspect ratio del logo).
 *   - ancho máximo se controla con `maxWidth` (default: 80px).
 */
export default function PaymentLogo({ id, size = 32, maxWidth = 80, className = "" }) {
  const common = {
    style: { height: size, maxWidth: `${maxWidth}px`, width: "auto", display: "block" },
    preserveAspectRatio: "xMidYMid meet",
    xmlns: "http://www.w3.org/2000/svg",
    className,
  };
  switch (id) {
    case "stripe":
      return (
        <svg {...common} viewBox="0 0 60 25" aria-label="Stripe">
          <path
            d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.63l.21 1.03c.56-.52 1.58-1.27 3.22-1.27 2.89 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.57-5.64 7.57zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.03-.86v3.54h3.12V9.1h-3.12v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66-1.4 0-2.94-.27-4.45-.92v-3.93c1.36.74 3.1 1.28 4.45 1.28.92 0 1.58-.25 1.58-1 0-1.95-6.37-1.22-6.37-5.85 0-2.9 2.21-4.63 5.55-4.63 1.34 0 2.68.2 4.02.74v3.88c-1.23-.66-2.8-1.04-4.03-1.04-.87 0-1.4.25-1.4.88 0 1.83 6.38 1.1 6.38 5.93z"
            fill="#635BFF"
          />
        </svg>
      );

    case "bbva":
      return (
        <svg {...common} viewBox="0 0 120 40" aria-label="BBVA">
          <text
            x="60"
            y="32"
            textAnchor="middle"
            fontFamily="'Arial Black', 'Helvetica Neue', Arial, sans-serif"
            fontWeight="900"
            fontSize="36"
            letterSpacing="1"
            fill="#004481"
          >
            BBVA
          </text>
        </svg>
      );

    case "santander":
      return (
        <svg {...common} viewBox="0 0 160 40" aria-label="Santander">
          {/* Llama simplificada */}
          <path
            d="M12 6 Q 22 16 17 26 Q 12 36 22 36 Q 32 36 27 26 Q 22 16 12 6 Z"
            fill="#EC0000"
          />
          <text
            x="40"
            y="30"
            fontFamily="'Helvetica Neue', Arial, sans-serif"
            fontWeight="700"
            fontSize="26"
            fill="#EC0000"
          >
            Santander
          </text>
        </svg>
      );

    case "hsbc":
      return (
        <svg {...common} viewBox="0 0 130 40" aria-label="HSBC">
          {/* Hexágono rojo oficial de HSBC (simplificado) */}
          <g transform="translate(0, 4)">
            <polygon points="16,0 32,16 16,32 0,16" fill="#DB0011" />
            <polygon points="16,6 24,16 16,26 8,16" fill="#ffffff" />
          </g>
          <text
            x="40"
            y="30"
            fontFamily="'Helvetica Neue', Arial, sans-serif"
            fontWeight="900"
            fontSize="28"
            letterSpacing="1"
            fill="#111111"
          >
            HSBC
          </text>
        </svg>
      );

    default:
      return null;
  }
}
